import React, { useContext } from "react";

import { ROLES, normalizeRole } from "../../../auth/access";
import { UserContext } from "../../../UserContext";
import { StudentInfoContext } from "./StudentInfoContext";
import { useProfileAlert } from "./useProfileAlert";
import {
   fmtIsoDDMMYYYY_HHMM,
   translateAttemptStatus,
} from "./studentInfoShared";
import { ReservationCardUI } from "../../UI";
import { examResultTone } from "./examResultTone";
import ProfileListMeta from "./ProfileListMeta";

function readExamTicketLabel(attempt) {
   const examTickets = Array.isArray(attempt?.examTickets)
      ? attempt.examTickets
      : Array.isArray(attempt?.tickets)
        ? attempt.tickets
        : [];
   const first = examTickets[0] || attempt?.ticket || null;
   const rawName = String(
      first?.ticket?.name ??
         first?.ticketName ??
         first?.name ??
         attempt?.ticketName ??
         "",
   ).trim();
   if (rawName) {
      const match = rawName.match(/(?:bilet(?:ul)?\s*)?(\d+)/i);
      return match?.[1] ? `Bilet ${match[1]}` : rawName;
   }

   const ticketId =
      first?.ticketId ?? first?.ticket?.id ?? attempt?.ticketId ?? null;
   return ticketId != null && String(ticketId).trim()
      ? `Bilet ${String(ticketId).trim()}`
      : "";
}


export default function StudentTheoryExamsTab() {
   const { user } = useContext(UserContext) || {};
   const role = normalizeRole(user?.role);
   const showTicketNumber = role === ROLES.ADMIN || role === ROLES.MANAGER;
   const {
      attemptsLoading,
      attemptsError,
      downloadError,
      attempts,
      handleDownloadPdf,
      downloadingId,
   } = useContext(StudentInfoContext);
   useProfileAlert(attemptsError);
   useProfileAlert(downloadError ? `Descărcare: ${downloadError}` : "");

   return (
      <div className="studentsProfileUI__attempts">
         {attemptsLoading && <p>Se încarcă încercările…</p>}

         {!attemptsLoading && !attemptsError && attempts.length === 0 && (
            <p className="students-info__empty">Nu există încercări.</p>
         )}

         {!attemptsLoading && !attemptsError && attempts.length > 0 && (
            // încercările, casete lipite ca rezervările (ReservationCardUI.List); descărcarea în colț
            <>
            <ProfileListMeta count={Math.min(attempts.length, 50)} one="încercare" many="încercări" />
            <ReservationCardUI.List>
               {attempts.slice(0, 50).map((a, idx) => {
                  const eid = a.examId ?? a.id;
                  const statusLabel = translateAttemptStatus(a.status || "UNKNOWN");
                  const ticketLabel = showTicketNumber ? readExamTicketLabel(a) : "";
                  const resultLabel = ticketLabel ? `${statusLabel} - ${ticketLabel}` : statusLabel;
                  // câmpurile serverului: score = răspunsuri corecte, totalQuestions, passScore = minimul
                  // pentru promovare, completedAt (cu numele vechi ca rezervă)
                  const correct = a.score ?? a.correct ?? null;
                  const total = a.totalQuestions ?? a.total ?? null;
                  const passScore = a.passScore ?? null;
                  const finishedAt = a.completedAt ?? a.finishedAt ?? null;
                  const started = a.startedAt ? fmtIsoDDMMYYYY_HHMM(a.startedAt) : "–";
                  const finished = finishedAt ? fmtIsoDDMMYYYY_HHMM(finishedAt) : null;
                  // rezultatul: „Corecte 23 din 24 · minim 22”; fără scor (încă în desfășurare) rândul lipsește
                  const resultLine =
                     correct != null
                        ? [`Corecte ${correct}${total ? ` din ${total}` : ""}`, passScore ? `minim ${passScore}` : ""].filter(Boolean).join(" · ")
                        : total
                          ? `${total} întrebări`
                          : null;
                  return (
                     <ReservationCardUI
                        key={(a.id ?? eid ?? "attempt") + "-" + idx}
                        tone={examResultTone(a.status)}
                        date={resultLabel}
                        lines={[finished ? `${started} → ${finished}` : started, resultLine]}
                        action={
                           eid
                              ? {
                                   icon: "download",
                                   label: "Descarcă rezultatul (PDF)",
                                   busy: downloadingId === eid,
                                   onClick: () => handleDownloadPdf(eid),
                                }
                              : null
                        }
                     />
                  );
               })}
            </ReservationCardUI.List>
            </>
         )}
      </div>
   );
}
